/**
 * Picks the production-credit total the project Payment Summary should display
 * (Overview tab "Add-ons & Cost Summary", the summary modal, and the customer
 * Completed/Review summary).
 *
 * Once the finance-ledger flag is on, Finance is the source of truth for credit
 * consumption — including credit-funded Social Iterations. The platform's static
 * `getOutputCreditAmount` can't see those: it hardcodes every non-master output
 * to 0, so a master(1) + credit-funded iteration(1) project rendered as 1 credit
 * on the Overview/Review surfaces while the Costs tab (which reads Finance)
 * correctly showed 2. This selector makes those surfaces defer to the
 * Finance-derived total so they agree with the Costs tab.
 *
 * Before Finance credits have loaded — or with the flag off — `financeProdCredits`
 * is null/undefined and we fall back to the legacy static per-output total, so
 * off-flag behaviour is unchanged.
 *
 * @param {Object} params
 * @param {?number} params.financeProdCredits Finance-derived production credits
 *   summed across all of the project's outputs, or null/undefined when Finance
 *   has not been read (flag off, or read still in flight).
 * @param {number} params.staticProdCredits Legacy static per-output total
 *   (sum of getOutputCreditAmount across outputs).
 * @returns {number}
 */
export function resolveDisplayedProdCredits({
  financeProdCredits,
  staticProdCredits
}) {
  if (financeProdCredits === null || financeProdCredits === undefined) {
    return staticProdCredits || 0;
  }
  return financeProdCredits;
}

/**
 * Picks the creative-services credit total the Payment Summary should display.
 *
 * Same finance-vs-static rule as `resolveDisplayedProdCredits`: once the
 * finance-ledger flag is on, Finance is the source of truth, so the summary's
 * "Creative Services" line item must use the Finance-derived value (read from
 * the master output's ledger record) — otherwise it shows the legacy
 * `getCreditsConsumption` value, which is 0 for ledger-only charges and made the
 * line read "0 Credit" while the Subtotal (which already sums the Finance value)
 * read higher. Before Finance has loaded — or with the flag off —
 * `financeCreativeServiceCredits` is null/undefined and we fall back to the
 * legacy value, so off-flag behaviour is unchanged.
 *
 * @param {Object} params
 * @param {?number} params.financeCreativeServiceCredits Finance-derived creative
 *   services credits (master output), or null/undefined when Finance has not
 *   been read (flag off, or read still in flight / unreachable).
 * @param {number} params.staticCreativeServiceCredits Legacy creative-services
 *   credits from `getCreditsConsumption`.
 * @returns {number}
 */
export function resolveDisplayedCreativeServiceCredits({
  financeCreativeServiceCredits,
  staticCreativeServiceCredits
}) {
  if (
    financeCreativeServiceCredits === null ||
    financeCreativeServiceCredits === undefined
  ) {
    return staticCreativeServiceCredits || 0;
  }
  return financeCreativeServiceCredits;
}

/**
 * Formats a credit count as the "N Credit(s)" label used across the summary.
 * Mirrors the existing inline `> 1` pluralisation (0 and 1 render "Credit").
 */
export function formatCreditsLabel(totalCredits) {
  return `${totalCredits} Credit${totalCredits > 1 ? 's' : ''}`;
}
